/**
 * Specific AI Workflow Node Types
 *
 * Each node type has its own styling and icon set based on functionality:
 * - DataRetrievalNode: Blue (data fetching)
 * - LLMAnalysisNode: Purple (AI processing)
 * - ReconciliationNode: Orange (merging results)
 * - HumanApprovalNode: Yellow (human decision)
 * - DecompositionNode: Teal (task breakdown)
 * - ImageGenerationNode: Pink (image generation)
 * - ToolExecutionNode: Cyan (tool/API execution)
 * - CodeExecutionNode: Lime (code execution)
 * - DefaultNode: Gray (fallback for unknown types)
 *
 * IMPORTANT: Use getNodeType() helper when setting node types to ensure
 * unknown types fall back to 'default' instead of rendering blank rectangles.
 */

import React from 'react';
import AIWorkflowBaseNode from './AIWorkflowBaseNode';
import { Database, Sparkles, GitMerge, User, ListTree, Image, Wrench, Code } from 'lucide-react';

/**
 * Data Retrieval Node
 * Used for fetching data from external sources
 */
export function DataRetrievalNode({ data }) {
  const nodeData = {
    ...data,
    color: '#3B82F6', // Blue
    nodeType: 'data_retrieval'
  };

  return (
    <AIWorkflowBaseNode data={nodeData}>
      <div className="flex items-center gap-2 text-blue-600 text-sm mb-2">
        <Database size={16} />
        <span className="font-medium">Retrieves data from external sources</span>
      </div>
      {data.retrievalSource && (
        <div className="text-xs text-gray-600">
          Source: {data.retrievalSource}
        </div>
      )}
    </AIWorkflowBaseNode>
  );
}

/**
 * LLM Analysis Node
 * Used for AI-powered analysis and processing
 */
export function LLMAnalysisNode({ data }) {
  const nodeData = {
    ...data,
    color: '#8B5CF6', // Purple
    nodeType: 'llm_analysis'
  };

  return (
    <AIWorkflowBaseNode data={nodeData}>
      <div className="flex items-center gap-2 text-purple-600 text-sm mb-2">
        <Sparkles size={16} />
        <span className="font-medium">AI-powered analysis</span>
      </div>
      {data.model && (
        <div className="text-xs text-gray-600">
          Model: {data.model}
        </div>
      )}
      {data.estimatedTokens && (
        <div className="text-xs text-gray-600">
          Est. tokens: {data.estimatedTokens}
        </div>
      )}
    </AIWorkflowBaseNode>
  );
}

/**
 * Reconciliation Node
 * Used for merging results from parallel branches
 */
export function ReconciliationNode({ data }) {
  const nodeData = {
    ...data,
    color: '#F97316', // Orange
    nodeType: 'reconciliation'
  };

  return (
    <AIWorkflowBaseNode data={nodeData}>
      <div className="flex items-center gap-2 text-orange-600 text-sm mb-2">
        <GitMerge size={16} />
        <span className="font-medium">Merges parallel results</span>
      </div>
      {data.branchCount && (
        <div className="text-xs text-gray-600">
          Merging {data.branchCount} branches
        </div>
      )}
    </AIWorkflowBaseNode>
  );
}

/**
 * Human Approval Node
 * Used for tasks requiring human judgment and approval
 */
export function HumanApprovalNode({ data }) {
  const nodeData = {
    ...data,
    color: '#EAB308', // Yellow
    nodeType: 'human_approval',
    requiresApproval: true
  };

  return (
    <AIWorkflowBaseNode data={nodeData}>
      <div className="flex items-center gap-2 text-yellow-600 text-sm mb-2">
        <User size={16} />
        <span className="font-medium">Requires human decision</span>
      </div>
      {data.approvalPrompt && (
        <div className="bg-yellow-50 rounded px-3 py-2 text-sm text-gray-700 mt-2">
          <div className="font-medium text-xs text-yellow-700 mb-1">Approval Required:</div>
          <div className="text-sm">{data.approvalPrompt}</div>
        </div>
      )}
      {data.riskLevel && (
        <div className="text-xs text-gray-600 mt-2">
          Risk Level: <span className="font-medium capitalize">{data.riskLevel}</span>
        </div>
      )}
    </AIWorkflowBaseNode>
  );
}

/**
 * Decomposition Node
 * Used for breaking down complex tasks into subtasks
 */
export function DecompositionNode({ data }) {
  const nodeData = {
    ...data,
    color: '#14B8A6', // Teal
    nodeType: 'decomposition'
  };

  return (
    <AIWorkflowBaseNode data={nodeData}>
      <div className="flex items-center gap-2 text-teal-600 text-sm mb-2">
        <ListTree size={16} />
        <span className="font-medium">Decomposes into subtasks</span>
      </div>
      {data.depthLevel !== undefined && (
        <div className="text-xs text-gray-600">
          Depth Level: {data.depthLevel}
        </div>
      )}
      {data.maxDepth && (
        <div className="text-xs text-gray-600">
          Max Depth: {data.maxDepth}
        </div>
      )}
    </AIWorkflowBaseNode>
  );
}

/**
 * Image Generation Node
 * Used for AI-powered image generation tasks
 */
export function ImageGenerationNode({ data }) {
  const nodeData = {
    ...data,
    color: '#EC4899', // Pink
    nodeType: 'image_generation'
  };

  return (
    <AIWorkflowBaseNode data={nodeData}>
      <div className="flex items-center gap-2 text-pink-600 text-sm mb-2">
        <Image size={16} />
        <span className="font-medium">Generates images</span>
      </div>
      {data.model && (
        <div className="text-xs text-gray-600">
          Model: {data.model}
        </div>
      )}
      {data.imageSize && (
        <div className="text-xs text-gray-600">
          Size: {data.imageSize}
        </div>
      )}
      {data.style && (
        <div className="text-xs text-gray-600">
          Style: {data.style}
        </div>
      )}
    </AIWorkflowBaseNode>
  );
}

/**
 * Tool Execution Node
 * Used for executing external tools and APIs
 */
export function ToolExecutionNode({ data }) {
  const nodeData = {
    ...data,
    color: '#06B6D4', // Cyan
    nodeType: 'tool_execution'
  };

  return (
    <AIWorkflowBaseNode data={nodeData}>
      <div className="flex items-center gap-2 text-cyan-600 text-sm mb-2">
        <Wrench size={16} />
        <span className="font-medium">Executes tool</span>
      </div>
      {data.toolName && (
        <div className="text-xs text-gray-600">
          Tool: {data.toolName}
        </div>
      )}
      {data.requiredTools && data.requiredTools.length > 0 && (
        <div className="text-xs text-gray-600">
          Tools: {data.requiredTools.join(', ')}
        </div>
      )}
    </AIWorkflowBaseNode>
  );
}

/**
 * Code Execution Node
 * Used for executing code snippets
 */
export function CodeExecutionNode({ data }) {
  const nodeData = {
    ...data,
    color: '#84CC16', // Lime
    nodeType: 'code_execution'
  };

  return (
    <AIWorkflowBaseNode data={nodeData}>
      <div className="flex items-center gap-2 text-lime-600 text-sm mb-2">
        <Code size={16} />
        <span className="font-medium">Executes code</span>
      </div>
      {data.language && (
        <div className="text-xs text-gray-600">
          Language: {data.language}
        </div>
      )}
    </AIWorkflowBaseNode>
  );
}

/**
 * Default/Unknown Node
 * Fallback for any unrecognized node types - ensures no blank rectangles
 */
export function DefaultNode({ data }) {
  const nodeData = {
    ...data,
    color: '#6B7280', // Gray
    nodeType: data.nodeType || 'unknown'
  };

  return (
    <AIWorkflowBaseNode data={nodeData}>
      <div className="flex items-center gap-2 text-gray-600 text-sm mb-2">
        <Sparkles size={16} />
        <span className="font-medium">
          {data.nodeType ? `${data.nodeType.replace(/_/g, ' ')}` : 'Processing'}
        </span>
      </div>
      {data.description && (
        <div className="text-xs text-gray-600">
          {data.description}
        </div>
      )}
    </AIWorkflowBaseNode>
  );
}

/**
 * Node type registry for React Flow
 * Maps node types to their components
 *
 * IMPORTANT: Use getNodeType() helper to resolve node types with fallback
 */
export const aiWorkflowNodeTypes = {
  data_retrieval: DataRetrievalNode,
  llm_analysis: LLMAnalysisNode,
  reconciliation: ReconciliationNode,
  human_approval: HumanApprovalNode,
  decomposition: DecompositionNode,
  image_generation: ImageGenerationNode,
  tool_execution: ToolExecutionNode,
  code_execution: CodeExecutionNode,
  default: DefaultNode
};

/**
 * Helper to resolve node type with fallback to 'default'
 * Ensures no blank rectangles for unknown node types
 */
export function getNodeType(nodeType) {
  if (nodeType && aiWorkflowNodeTypes[nodeType]) {
    return nodeType;
  }
  return 'default';
}
